import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionDepartmentsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as ToggleActions from "../../../actions/pages/togglePage";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { TogglePageItem } from "../../../reducers/pages/togglePage";

import "./InstitutionDepartmentsPage.scss";

interface IInstitutionDepartmentPageParams {
    institutionId: number;
}

interface IInstitutionDepartmentPageProps extends IRouterProps<IInstitutionDepartmentPageParams> {
    institutionId: number;

    // From state
    institution: Dtos.Institution;
    departments: Dtos.Department[];

    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    toggleItems: TogglePageItem[];
}

interface IInstitutionDepartmentPageActions extends IRouterActions {
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    toggleExpand: ToggleActions.IToggleExpandedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type InstitutionDepartmentPageProps = IInstitutionDepartmentPageProps &
    IInstitutionDepartmentPageActions;

class InstitutionDepartmentPage extends React.PureComponent<
    InstitutionDepartmentPageProps,
    any
> {
    constructor(props: InstitutionDepartmentPageProps) {
        super(props);

        this.clearInstitution = this.clearInstitution.bind(this);
    }

    componentDidMount() {
        const {
            loadInstitution,
            institutionId,
            setDrawerItems,
            setBreadcrumbs,
            permissions
        } = this.props;
        

        setDrawerItems(InstitutionSubMenu(institutionId, permissions));
        setBreadcrumbs(InstitutionDepartmentsBreadcrumbs(institutionId));

        loadInstitution(institutionId);
    }

    componentWillReceiveProps(_nextProps: InstitutionDepartmentPageProps) {}

    componentDidUpdate(_prevProps: InstitutionDepartmentPageProps, _prevState) {
        const {
            navigate,
            institution,
            institutionId,
            setDrawerItems,
            permissions,
            setBreadcrumbs,
            loadInstitutionFailure,
            loadInstitutionErrorCode,
        } = this.props;

        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }

        if (institutionId && institution?.name) {
            setDrawerItems(InstitutionSubMenu(institutionId, permissions));
            setBreadcrumbs(
                InstitutionDepartmentsBreadcrumbs(
                    institutionId,
                    institution.name
                )
            );
        }
    }

    componentWillUpdate(_nextProps: InstitutionDepartmentPageProps) {}

    componentWillUnmount() {
        this.clearInstitution();
    }

    clearInstitution() {
        this.props.clearInstitution();
    }

    showInstitution(): boolean {
        const { loadingInstitution, loadInstitutionSuccess } = this.props;

        if (!loadingInstitution && loadInstitutionSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const { loadingInstitution } = this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.InstitutionDepartmentView}
                loading={loadingInstitution}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const {
            institution,
            loadingInstitution,
            permissions,
            institutionId,
        } = this.props;

        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Institution Departments</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionDepartmentCreate,
                            null,
                            institutionId
                        ) &&
                            institution &&
                            institution.id > 0 && (
                                <>
                                    <Link
                                        className={"btn btn-primary"}
                                        url={
                                            "/institutions/" +
                                            institution.id +
                                            "/departments/create"
                                        }
                                    >
                                        Create
                                    </Link>
                                </>
                            )}
                    </div>
                </div>
                {loadingInstitution ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {!loadingInstitution ? (
                    <>
                        {this.renderTable()}
                    </>
                ) : null}
            </div>
        );
    }

    renderTable() {
        const { departments } = this.props;

        if (!departments || departments.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no departments to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = departments.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={departments}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? DEFAULT_PAGE_SIZE : departments.length
                    }
                    pageSize={showPagination ? undefined : departments.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no departments to display"
                    resizable={false}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string =
                                    "/institutions/" +
                                    rowInfo.original.institutionId +
                                    "/departments/" +
                                    rowInfo.original.id;
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: InstitutionDepartmentPageProps
): IInstitutionDepartmentPageProps => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        match: ownProps.match,
        institutionId: ownProps.match
            ? ownProps.match.params.institutionId
            : undefined!,

        history: ownProps.history,
        location: ownProps.location,

        institution: institution!,
        departments: institution ? institution.departments : undefined,

        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,
        toggleItems: state.togglePage.items,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: InstitutionDepartmentPageProps
): IInstitutionDepartmentPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadInstitution: bindActionCreators(
            InstitutionActions.LoadInstitutionById,
            dispatch
        ),
        clearInstitution: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        ),

        toggleExpand: bindActionCreators(
            ToggleActions.ToggleExpanded,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<
    IInstitutionDepartmentPageParams,
    InstitutionDepartmentPageProps
>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(
        InstitutionDepartmentPage
    ) as unknown as ComponentType<InstitutionDepartmentPageProps>
) as ComponentType<any>;
